import React from 'react';
import { Zap, ShieldCheck, Flame, Cpu, ArrowUpRight, Activity, Database } from 'lucide-react';
import './EnergySystems.css';

const EnergySystems = () => {
  const domains = [
    {
      icon: Zap,
      accentClass: "accent-amber",
      title: "Power Grid & Electricity Load Dynamics",
      subtitle: "Spatio-Temporal Grid Forecasting",
      description: "Regional power grids operate on sub-second physical dynamics driven by weather patterns, thermal dissipation, and renewable volatility. We build spatio-temporal AI models to forecast localized nodal load demand, transmission congestion, and locational marginal pricing across major ISOs.",
      highlights: [
        "Multi-node graph neural networks for regional power grid topology",
        "Probabilistic load forecasting under extreme weather anomalies",
        "AI data-center power demand integration into regional dispatch models"
      ]
    },
    {
      icon: Flame,
      accentClass: "accent-orange",
      title: "Natural Gas & Physical Fuel Supply Chains",
      subtitle: "Pipeline Constraints & Storage Deliverability",
      description: "Natural gas is the primary swing fuel balancing electrical grids during peak compute demand. Our research models physical gas pipeline throughput constraints, storage injection/withdrawal dynamics, and regional deliverability under extreme winter and summer peak load events.",
      highlights: [
        "Physics-informed neural models for pipeline flow dynamics",
        "Real-time anomaly detection in natural gas storage deliverability",
        "Cross-commodity modeling between natural gas supply and power burn"
      ]
    },
    {
      icon: Cpu,
      accentClass: "accent-blue",
      title: "AI Compute Expansion & Grid Co-Optimization",
      subtitle: "GW-Scale Data Center Power Modeling",
      description: "The rapid expansion of hyper-scale AI clusters adds gigawatts of baseload demand to electricity grids. We analyze the spatial footprint of AI compute expansion and develop closed-loop models to co-optimize data center workload dispatch with power grid capacity constraints.",
      highlights: [
        "Spatial clustering analysis for data center grid interconnections",
        "Workload flexibility modeling for demand-side grid balancing",
        "Long-term infrastructure capacity planning under AI scaling"
      ]
    }
  ];

  const keyMetrics = [
    {
      icon: Activity,
      label: "Spatio-Temporal Resolution",
      value: "Nodal Grid Level"
    },
    {
      icon: Database,
      label: "Commodities Modeled",
      value: "Power & Natural Gas"
    },
    {
      icon: ShieldCheck,
      label: "Deployment Environment",
      value: "Production Systematic AI"
    }
  ];

  return (
    <div className="energy-page">
      {/* Header Banner */}
      <header className="energy-header glass">
        <div className="energy-badge">
          <Zap className="badge-icon" />
          Mercuria Energy America Focus
        </div>
        <h1>AI Systems for Energy Markets & Critical Infrastructure</h1>
        <p className="energy-lead">
          Optimizing power grids, natural gas networks, and commodity physical dynamics at the intersection of AI compute scaling and global energy transitions.
        </p>

        {/* Quick Highlights Bar */}
        <div className="energy-metrics-bar">
          {keyMetrics.map((m, idx) => {
            const IconComponent = m.icon;
            return (
              <div key={idx} className="metric-pill glass">
                <IconComponent className="metric-icon" />
                <div className="metric-text">
                  <span className="metric-label">{m.label}</span>
                  <span className="metric-value">{m.value}</span>
                </div>
              </div>
            );
          })}
        </div>
      </header>

      {/* Conceptual Overview */}
      <section className="energy-overview glass">
        <div className="overview-header">
          <span className="overview-kicker">Core Scientific Thesis</span>
          <h2>The Physical Reality of Global Energy Systems</h2>
        </div>
        <p>
          Unlike pure digital software, energy markets are governed by physical laws: <strong>Kirchhoff’s circuit laws, fluid thermodynamics in pipelines, generator ramp rates, and strict transmission capacity bounds</strong>. 
        </p>
        <p>
          At <strong>Mercuria Energy</strong>, my work translates advanced machine learning—spatio-temporal neural networks, state-space representations, and non-stationary forecasting—into production systems that model real-world power and natural gas markets.
        </p>
      </section>

      {/* Technical Focus Areas */}
      <section className="domains-section">
        <div className="section-header-center">
          <span className="section-kicker">Research & Engineering Thrusts</span>
          <h2>Technical Focus Areas</h2>
        </div>

        <div className="domains-grid">
          {domains.map((domain, index) => {
            const IconComp = domain.icon;
            return (
              <div key={index} className={`domain-card glass glass-hover ${domain.accentClass}`}>
                <div className="domain-header">
                  <div className="domain-icon-bg">
                    <IconComp className="domain-icon" />
                  </div>
                  <div>
                    <span className="domain-subtitle">{domain.subtitle}</span>
                    <h3>{domain.title}</h3>
                  </div>
                </div>
                <p className="domain-desc">{domain.description}</p>
                <ul className="highlights-list">
                  {domain.highlights.map((h, i) => (
                    <li key={i}>
                      <ArrowUpRight className="bullet-arrow" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* High-Stakes Real-World Deployment Impact */}
      <section className="energy-impact glass">
        <div className="impact-inner">
          <ShieldCheck className="impact-icon" />
          <div>
            <h3>High-Stakes Real-World Deployment</h3>
            <p>
              These models operate in production environments where market liquidity, grid stability, and physical deliverability are essential. The resulting insights bridge high-performance AI research with real-world energy market execution.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnergySystems;
